import { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Upload, Check, Loader2, AlertCircle, Link as LinkIcon, Lock, Sparkles, RefreshCw, AlertTriangle } from 'lucide-react';
import { useAuth, authHeaders } from '@/contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DISCIPLINE_OPTIONS = [
  'Skeet',
  'Skeet Olympique',
  'DTL',
  'Fosse Olympique',
  'Fosse Américaine',
  'Parcours de Chasse',
  'Compak Sporting',
  'Helice',
];

const ALLOWED_MIME_PREFIXES = ['image/', 'application/pdf'];

export default function BallTrapCampagneSoumettre() {
  const { user, token, loading: authLoading } = useAuth();
  const [form, setForm] = useState({
    name: '',
    organizer: '',
    address: '',
    city: '',
    postal_code: '',
    department: '',
    date_start: '',
    date_end: '',
    opening_hours: '',
    disciplines: [],
    price_info: '',
    description: '',
    phone: '',
    email: '',
    website: '',
    contact_email: '',
  });
  const [posterFile, setPosterFile] = useState(null);
  const [posterPreview, setPosterPreview] = useState(null); // data URL or null
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [extractInfo, setExtractInfo] = useState(''); // last extraction result message
  const [duplicates, setDuplicates] = useState([]);
  const [duplicatesAck, setDuplicatesAck] = useState(false); // user has accepted to submit despite duplicates
  const [checkingDup, setCheckingDup] = useState(false);

  // Debounced duplicate check whenever location/date fields change.
  useEffect(() => {
    const name = form.name.trim();
    const city = form.city.trim();
    const postal = form.postal_code.trim();
    const ds = form.date_start.trim();
    if (!ds && !postal && !city) { setDuplicates([]); return; }
    if (!postal && !city) { setDuplicates([]); return; }
    setCheckingDup(true);
    const handle = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        if (name) params.set('name', name);
        if (city) params.set('city', city);
        if (postal) params.set('postal_code', postal);
        if (form.department) params.set('department', form.department);
        if (ds) params.set('date_start', ds);
        if (form.date_end) params.set('date_end', form.date_end);
        const r = await axios.get(`${API}/country-events/check-duplicate?${params}`);
        setDuplicates(Array.isArray(r.data?.duplicates) ? r.data.duplicates : []);
        setDuplicatesAck(false);
      } catch { setDuplicates([]); }
      finally { setCheckingDup(false); }
    }, 700);
    return () => clearTimeout(handle);
  }, [form.name, form.city, form.postal_code, form.department, form.date_start, form.date_end]);

  function update(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function toggleDiscipline(d) {
    setForm((f) => ({
      ...f,
      disciplines: f.disciplines.includes(d)
        ? f.disciplines.filter((x) => x !== d)
        : [...f.disciplines, d],
    }));
  }

  async function extractPoster() {
    if (!posterFile || extracting) return;
    setExtracting(true);
    setError('');
    setExtractInfo('');
    try {
      const data = new FormData();
      data.append('poster', posterFile);
      const r = await axios.post(`${API}/country-events/extract-poster`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });
      const extracted = r.data?.data || {};
      const count = r.data?.fields_extracted || 0;
      // Merge extracted values without overwriting fields the user has already typed
      setForm((f) => {
        const next = { ...f };
        for (const k of Object.keys(extracted)) {
          if (k === 'disciplines') {
            // Merge sets — keep user's choices, add extracted ones
            const existing = new Set(f.disciplines || []);
            (extracted.disciplines || []).forEach((d) => {
              if (DISCIPLINE_OPTIONS.includes(d)) existing.add(d);
            });
            next.disciplines = Array.from(existing);
          } else if (extracted[k] && (!f[k] || String(f[k]).trim() === '')) {
            next[k] = extracted[k];
          }
        }
        return next;
      });
      if (count === 0) {
        setExtractInfo("L'IA n'a pas pu lire cette image. Vérifiez qu'il s'agit bien d'une affiche lisible (texte net) ou remplissez les champs manuellement.");
      } else {
        setExtractInfo(`✨ ${count} champ${count > 1 ? 's' : ''} extrait${count > 1 ? 's' : ''}. Vérifiez les valeurs avant d'enregistrer.`);
      }
    } catch (err) {
      const msg = err.response?.data?.error || "Extraction IA indisponible. Remplissez le formulaire manuellement.";
      setExtractInfo(msg);
    } finally {
      setExtracting(false);
    }
  }

  function handleFile(e) {
    const file = e.target.files?.[0];
    setError('');
    if (!file) { setPosterFile(null); setPosterPreview(null); return; }
    if (!ALLOWED_MIME_PREFIXES.some((p) => file.type.startsWith(p))) {
      setError('Format non supporté. Utilisez une image (JPG/PNG/WEBP/HEIC…) ou un PDF.');
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      setError('Fichier trop volumineux (max 15 Mo).');
      return;
    }
    setPosterFile(file);
    // Preview for images only (skip PDFs)
    if (file.type.startsWith('image/') && file.type !== 'image/heic' && file.type !== 'image/heif') {
      const reader = new FileReader();
      reader.onload = (ev) => setPosterPreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      setPosterPreview(null);
    }
  }

  async function submit(e) {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!form.name.trim() || form.name.trim().length < 3) {
      setError("Le nom de l'évènement est requis.");
      return;
    }
    if (!form.postal_code.trim() && !form.department.trim()) {
      setError('Code postal ou département requis.');
      return;
    }
    if (duplicates.length > 0 && !duplicatesAck) {
      setError("Un évènement très similaire existe déjà. Vérifiez la liste ci-dessous, puis confirmez si vous souhaitez quand même publier votre déclaration.");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setSubmitting(true);
    const data = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (k === 'disciplines') data.append(k, v.join(','));
      else data.append(k, v);
    });
    if (posterFile) data.append('poster', posterFile);
    try {
      const r = await axios.post(`${API}/country-events`, data, {
        headers: { 'Content-Type': 'multipart/form-data', ...authHeaders(token) },
      });
      setSuccess(r.data?.message || 'Votre évènement a bien été soumis.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.response?.data?.error || 'Une erreur est survenue. Réessayez.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white font-['Manrope']">
      <div className="fixed inset-0 -z-10 opacity-40 pointer-events-none"
        style={{ background: 'radial-gradient(circle at 20% 0%, rgba(255,193,7,0.12), transparent 50%)' }} />

      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#0D0D0D]/80 border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/ball-trap-campagne" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors" data-testid="back-list-link">
            <ArrowLeft className="w-4 h-4" /> Retour à la liste
          </Link>
          {user && (
            <Link to="/mon-espace" className="text-xs text-[#A0A0A0] hover:text-white">
              {user.email}
            </Link>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 pt-8 pb-24">
        {!authLoading && !user ? (
          <div className="rounded-3xl bg-gradient-to-br from-[#FFC107]/15 via-[#141414] to-[#FF6B35]/10 border border-[#FFC107]/30 p-8 sm:p-12 text-center" data-testid="auth-required">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#FFC107]/20 text-[#FFC107] mb-5">
              <Lock className="w-10 h-10" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-black mb-3">Compte requis</h1>
            <p className="text-[#C8C8C8] leading-relaxed mb-6 max-w-lg mx-auto">
              Pour publier un ball-trap, vous devez d'abord créer un compte ClayPulse.
              <br />
              <strong className="text-[#FFC107]">C'est 100% gratuit et prend 30 secondes.</strong>
            </p>
            <div className="rounded-xl bg-black/40 border border-white/10 p-4 mb-6 text-left max-w-md mx-auto">
              <p className="text-xs uppercase tracking-widest text-[#888] font-bold mb-2">Pourquoi un compte&nbsp;?</p>
              <ul className="space-y-1.5 text-sm text-[#C8C8C8]">
                <li>✦ Modifier ou annuler vos évènements à tout moment</li>
                <li>✦ Retrouver tous vos évènements dans votre espace</li>
                <li>✦ Recevoir un email à chaque validation / refus</li>
              </ul>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link to="/register" className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-gradient-to-r from-[#FFC107] to-[#FF9800] text-black font-bold hover:scale-[1.03] transition-transform" data-testid="cta-register">
                Créer mon compte gratuit
              </Link>
              <Link to="/login" className="inline-flex items-center gap-2 px-7 py-3 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/30" data-testid="cta-login">
                Se connecter
              </Link>
            </div>
          </div>
        ) : success ? (
          <div className="rounded-3xl bg-gradient-to-br from-green-500/20 via-[#141414] to-[#FFC107]/10 border border-green-500/30 p-8 sm:p-12 text-center" data-testid="submit-success">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500/20 text-green-300 mb-6">
              <Check className="w-10 h-10" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-black mb-3">Évènement envoyé !</h1>
            <p className="text-[#C8C8C8] leading-relaxed mb-8 max-w-xl mx-auto">{success}</p>

            <div className="rounded-xl bg-[#FFC107]/10 border border-[#FFC107]/40 p-5 mb-8 text-left max-w-xl mx-auto" data-testid="manage-account-block">
              <div className="flex items-center gap-2 mb-2">
                <LinkIcon className="w-4 h-4 text-[#FFC107]" />
                <p className="text-sm font-bold text-[#FFC107] uppercase tracking-widest">Gérer mon évènement</p>
              </div>
              <p className="text-xs text-[#E0E0E0] leading-relaxed mb-3">
                Vous pouvez modifier ou annuler votre évènement à tout moment depuis <strong>« Mon espace »</strong> sur votre compte ClayPulse.
              </p>
              <Link to="/mon-espace" data-testid="goto-dashboard-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#FFC107] text-black text-xs font-bold hover:bg-[#FFB300]">
                Aller à Mon espace
              </Link>
            </div>

            <div className="rounded-xl bg-black/40 border border-white/10 p-5 mb-8 text-left max-w-md mx-auto">
              <p className="text-xs uppercase tracking-widest text-[#888] font-bold mb-2">Et maintenant&nbsp;?</p>
              <ul className="space-y-2 text-sm text-[#C8C8C8]">
                <li className="flex items-start gap-2"><span className="text-[#FFC107] font-bold">1.</span> L'équipe ClayPulse vérifie les informations.</li>
                <li className="flex items-start gap-2"><span className="text-[#FFC107] font-bold">2.</span> Une fois validé, votre évènement apparaît sur la carte.</li>
                <li className="flex items-start gap-2"><span className="text-[#FFC107] font-bold">3.</span> Délai habituel&nbsp;: <strong>moins de 24&nbsp;h ouvrées</strong>.</li>
              </ul>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link to="/ball-trap-campagne" className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-gradient-to-r from-[#FFC107] to-[#FF9800] text-black font-bold hover:scale-[1.03] transition-transform" data-testid="back-to-map-btn">
                Retour à la carte
              </Link>
              <button
                onClick={() => {
                  setSuccess(''); setError('');
                  setForm({
                    name: '', organizer: '', address: '', city: '', postal_code: '', department: '',
                    date_start: '', date_end: '', opening_hours: '', disciplines: [],
                    price_info: '', description: '', phone: '', email: '', website: '', contact_email: '',
                  });
                  setPosterFile(null); setPosterPreview(null);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="inline-flex items-center gap-2 px-7 py-3 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/30 transition-colors"
                data-testid="submit-another-btn"
              >
                Déclarer un autre évènement
              </button>
            </div>
          </div>
        ) : (
        <>
        <h1 className="text-3xl sm:text-4xl font-black mb-2">Déclarer un ball-trap de campagne</h1>
        <p className="text-[#C8C8C8] mb-8 leading-relaxed">
          Publication gratuite · Votre évènement sera visible après validation par l'équipe ClayPulse (sous 24 h ouvrées).
        </p>

        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-500/15 border border-red-500/30 text-red-200 flex items-start gap-3" data-testid="submit-error">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        <AIExplainerCard />

        {duplicates.length > 0 && (
          <div className="mb-6 rounded-xl bg-amber-500/10 border border-amber-500/40 p-4" data-testid="duplicate-banner">
            <div className="flex items-start gap-3 mb-3">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-300" />
              <div className="flex-1">
                <p className="text-sm font-bold text-amber-200">
                  Un évènement très similaire existe déjà ({duplicates.length})
                </p>
                <p className="text-xs text-[#C8C8C8] mt-1 leading-relaxed">
                  Vérifiez si votre ball-trap a déjà été déclaré. Si vous souhaitez quand même soumettre, cochez la case en bas.
                </p>
              </div>
            </div>
            <ul className="space-y-2 mb-3">
              {duplicates.map((d) => (
                <li key={d.id} className="text-xs flex items-center justify-between gap-2 p-2 rounded-lg bg-black/30 border border-white/5" data-testid={`dup-item-${d.id}`}>
                  <div className="min-w-0">
                    <div className="font-bold text-white truncate">{d.name || '(sans nom)'}</div>
                    <div className="text-[11px] text-[#888]">
                      {[d.city, d.postal_code, d.date_start, d.status === 'pending' ? '· en validation' : ''].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/15 text-amber-300 border border-amber-500/30 shrink-0">
                    {d._match_score}% similaire
                  </span>
                </li>
              ))}
            </ul>
            <label className="flex items-center gap-2 text-xs text-[#C8C8C8] cursor-pointer">
              <input
                type="checkbox"
                checked={duplicatesAck}
                onChange={(e) => setDuplicatesAck(e.target.checked)}
                className="w-4 h-4 accent-amber-400"
                data-testid="dup-ack-checkbox"
              />
              Je confirme que mon évènement est différent et je souhaite quand même le publier.
            </label>
          </div>
        )}

        <form onSubmit={submit} className="space-y-6" data-testid="submit-form">
          {/* Section 1 */}
          <fieldset className="rounded-2xl bg-gradient-to-br from-[#141414] to-[#0B0B0B] border border-white/5 p-5 sm:p-6">
            <legend className="px-2 text-xs uppercase tracking-widest text-[#FFC107] font-bold">1 · L'évènement</legend>
            <div className="space-y-4 mt-2">
              <Field label="Nom de l'évènement *" >
                <input required value={form.name} onChange={(e) => update('name', e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none transition-colors"
                  placeholder="Ex: Ball-Trap de la Saint-Hubert" data-testid="field-name" />
              </Field>
              <Field label="Club ou organisateur">
                <input value={form.organizer} onChange={(e) => update('organizer', e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none"
                  placeholder="Ex: Société de Chasse de ..." data-testid="field-organizer" />
              </Field>
              <Field label="Description / infos pratiques">
                <textarea rows={4} value={form.description} onChange={(e) => update('description', e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none resize-y"
                  placeholder="Repas sur place, cartouches obligatoires, prix d'inscription, etc." data-testid="field-description" />
              </Field>
            </div>
          </fieldset>

          {/* Section 2: Lieu */}
          <fieldset className="rounded-2xl bg-gradient-to-br from-[#141414] to-[#0B0B0B] border border-white/5 p-5 sm:p-6">
            <legend className="px-2 text-xs uppercase tracking-widest text-[#FFC107] font-bold">2 · Lieu</legend>
            <div className="space-y-4 mt-2">
              <Field label="Adresse">
                <input value={form.address} onChange={(e) => update('address', e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none"
                  placeholder="Ex: Route des Voyettes" data-testid="field-address" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Code postal *">
                  <input value={form.postal_code} onChange={(e) => update('postal_code', e.target.value)}
                    maxLength={5}
                    className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none"
                    placeholder="60430" data-testid="field-postal-code" />
                </Field>
                <Field label="Ville">
                  <input value={form.city} onChange={(e) => update('city', e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none"
                    placeholder="Silly-Tillard" data-testid="field-city" />
                </Field>
              </div>
            </div>
          </fieldset>

          {/* Section 3: Dates */}
          <fieldset className="rounded-2xl bg-gradient-to-br from-[#141414] to-[#0B0B0B] border border-white/5 p-5 sm:p-6">
            <legend className="px-2 text-xs uppercase tracking-widest text-[#FFC107] font-bold">3 · Dates & tarifs</legend>
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Date de début">
                  <input type="date" value={form.date_start} onChange={(e) => update('date_start', e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none"
                    data-testid="field-date-start" />
                </Field>
                <Field label="Date de fin (si plusieurs jours)">
                  <input type="date" value={form.date_end} onChange={(e) => update('date_end', e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none"
                    data-testid="field-date-end" />
                </Field>
              </div>
              <Field label="Horaires d'ouverture">
                <input value={form.opening_hours} onChange={(e) => update('opening_hours', e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none"
                  placeholder="Ex: 9h-18h" data-testid="field-hours" />
              </Field>
              <Field label="Tarifs">
                <input value={form.price_info} onChange={(e) => update('price_info', e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none"
                  placeholder="Ex: 8€ la série de 25, inscription 15€" data-testid="field-prices" />
              </Field>
            </div>
          </fieldset>

          {/* Section 4: Disciplines */}
          <fieldset className="rounded-2xl bg-gradient-to-br from-[#141414] to-[#0B0B0B] border border-white/5 p-5 sm:p-6">
            <legend className="px-2 text-xs uppercase tracking-widest text-[#FFC107] font-bold">4 · Disciplines proposées</legend>
            <div className="flex flex-wrap gap-2 mt-2">
              {DISCIPLINE_OPTIONS.map((d) => {
                const active = form.disciplines.includes(d);
                return (
                  <button
                    type="button"
                    key={d}
                    onClick={() => toggleDiscipline(d)}
                    className={`px-3.5 py-2 text-sm rounded-full border transition-all ${active
                      ? 'bg-[#FFC107] border-[#FFC107] text-black font-bold'
                      : 'bg-white/5 border-white/10 text-[#C8C8C8] hover:border-[#FFC107]/40'}`}
                    data-testid={`discipline-${d.replace(/\s/g, '-').toLowerCase()}`}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
          </fieldset>

          {/* Section 5: Contact */}
          <fieldset className="rounded-2xl bg-gradient-to-br from-[#141414] to-[#0B0B0B] border border-white/5 p-5 sm:p-6">
            <legend className="px-2 text-xs uppercase tracking-widest text-[#FFC107] font-bold">5 · Contact public</legend>
            <p className="text-xs text-[#888] mt-1 mb-3">Ces infos seront affichées aux tireurs.</p>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Téléphone">
                  <input value={form.phone} onChange={(e) => update('phone', e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none"
                    placeholder="06 12 34 56 78" data-testid="field-phone" />
                </Field>
                <Field label="Email">
                  <input type="email" value={form.email} onChange={(e) => update('email', e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none"
                    placeholder="contact@club.fr" data-testid="field-email-public" />
                </Field>
              </div>
              <Field label="Site web ou page Facebook">
                <input type="url" value={form.website} onChange={(e) => update('website', e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none"
                  placeholder="https://…" data-testid="field-website" />
              </Field>
              <Field label="Votre email (privé, pour vous prévenir de la validation)">
                <input type="email" value={form.contact_email} onChange={(e) => update('contact_email', e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none"
                  placeholder="votre@email.fr" data-testid="field-contact-email" />
              </Field>
            </div>
          </fieldset>

          {/* Section 6: Affiche */}
          <fieldset id="poster-section" className="rounded-2xl bg-gradient-to-br from-[#141414] to-[#0B0B0B] border border-white/5 p-5 sm:p-6 transition-shadow">
            <legend className="px-2 text-xs uppercase tracking-widest text-[#FFC107] font-bold">6 · Affiche</legend>
            <p className="text-xs text-[#888] mt-1 mb-3">JPG, PNG, WEBP, HEIC ou PDF · 15 Mo max.</p>
            <label className="block cursor-pointer">
              <input type="file" accept="image/*,application/pdf" onChange={handleFile} className="hidden" data-testid="field-poster" />
              <div className="border-2 border-dashed border-white/15 rounded-xl p-6 hover:border-[#FFC107]/50 transition-colors text-center">
                {posterPreview ? (
                  <>
                    <img src={posterPreview} alt="Aperçu" className="max-h-56 mx-auto rounded-lg shadow-lg mb-3" data-testid="poster-preview" />
                    <p className="text-xs text-[#888]">Cliquez pour changer</p>
                  </>
                ) : posterFile ? (
                  <>
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#FFC107]/15 text-[#FFC107] mb-2">
                      <Check className="w-6 h-6" />
                    </div>
                    <p className="text-sm text-white">{posterFile.name}</p>
                    <p className="text-xs text-[#888] mt-1">Cliquez pour changer</p>
                  </>
                ) : (
                  <>
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/5 text-white/60 mb-2">
                      <Upload className="w-6 h-6" />
                    </div>
                    <p className="text-sm text-white font-medium">Glissez ou cliquez pour ajouter l'affiche</p>
                    <p className="text-xs text-[#888] mt-1">Images & PDF acceptés</p>
                  </>
                )}
              </div>
            </label>

            {posterFile && (
              <div className="mt-4">
                <button
                  type="button"
                  onClick={extractPoster}
                  disabled={extracting}
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-400/40 text-purple-200 font-bold hover:from-purple-600/30 hover:to-pink-600/30 hover:text-white disabled:opacity-60 transition-all"
                  data-testid="extract-poster-btn"
                >
                  {extracting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> L'IA lit votre affiche…</>
                  ) : extractInfo ? (
                    <><RefreshCw className="w-4 h-4" /> Réessayer l'extraction</>
                  ) : (
                    <><Sparkles className="w-4 h-4" /> ✨ Auto-remplir avec l'IA</>
                  )}
                </button>
                {extractInfo && (
                  <p className={`mt-2 text-xs text-center ${extractInfo.startsWith('✨') ? 'text-purple-300' : 'text-[#888]'}`} data-testid="extract-info">
                    {extractInfo}
                  </p>
                )}
              </div>
            )}
          </fieldset>

          <div className="flex items-center justify-end gap-3 pt-2">
            <Link to="/ball-trap-campagne" className="px-6 py-3 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/30 transition-colors" data-testid="cancel-btn">
              Annuler
            </Link>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-gradient-to-r from-[#FFC107] to-[#FF9800] text-black font-bold disabled:opacity-60 hover:scale-[1.03] transition-transform"
              data-testid="submit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {submitting ? 'Envoi…' : 'Publier mon évènement'}
            </button>
          </div>
        </form>
        </>
        )}
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-[#C8C8C8] mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function AIExplainerCard() {
  const [expanded, setExpanded] = useState(true);
  function scrollToPoster() {
    const el = document.getElementById('poster-section');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Brief flash to draw attention
      el.style.transition = 'box-shadow 0.6s ease';
      el.style.boxShadow = '0 0 0 3px rgba(168,85,247,0.6), 0 0 40px rgba(168,85,247,0.4)';
      setTimeout(() => { el.style.boxShadow = ''; }, 1800);
    }
  }
  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="mb-6 w-full text-left p-3 rounded-xl bg-purple-600/10 border border-purple-400/30 text-xs text-purple-200 hover:bg-purple-600/15 transition-colors inline-flex items-center gap-2"
        data-testid="ai-explainer-reopen"
      >
        <Sparkles className="w-3.5 h-3.5" /> Astuce IA — gagner 2 minutes (cliquez pour rouvrir)
      </button>
    );
  }
  return (
    <div className="relative mb-8 overflow-hidden rounded-2xl bg-gradient-to-br from-purple-600/20 via-purple-700/10 to-pink-600/15 border border-purple-400/30 p-5 sm:p-6" data-testid="ai-explainer">
      <button
        onClick={() => setExpanded(false)}
        aria-label="Fermer"
        className="absolute top-3 right-3 w-7 h-7 rounded-full bg-black/30 border border-white/10 text-white/60 hover:text-white hover:bg-black/50 inline-flex items-center justify-center text-sm"
        data-testid="ai-explainer-close"
      >
        ×
      </button>
      {/* Decorative sparkles */}
      <div className="absolute -top-8 -right-12 w-40 h-40 rounded-full bg-purple-500/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-10 -left-8 w-32 h-32 rounded-full bg-pink-500/15 blur-3xl pointer-events-none" />

      <div className="relative">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-600/30 border border-purple-400/40 mb-3">
          <Sparkles className="w-3.5 h-3.5 text-purple-200" />
          <span className="text-[11px] font-bold uppercase tracking-widest text-purple-100">Avec l'IA</span>
        </div>

        <h2 className="text-xl sm:text-2xl font-black mb-2">
          Déclarez votre ball-trap en <span className="text-purple-300">20 secondes</span>
        </h2>
        <p className="text-sm text-[#C8C8C8] mb-5 leading-relaxed max-w-xl">
          Pas envie de tout retaper ? Donnez-nous l'affiche, l'IA s'occupe du reste — nom, dates, lieu, contacts…
        </p>

        {/* 3 steps */}
        <ol className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
          {[
            { n: 1, t: "Téléversez l'affiche", d: "Photo, JPG, PNG ou PDF" },
            { n: 2, t: "Cliquez « Auto-remplir »", d: "L'IA lit votre affiche en 5-7 s" },
            { n: 3, t: "Vérifiez et publiez", d: "Corrigez si besoin, puis envoyez" },
          ].map((s) => (
            <li key={s.n} className="flex gap-3 p-3 rounded-xl bg-black/25 border border-white/5">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 text-white font-black flex items-center justify-center text-sm shadow-lg shadow-purple-500/30">
                {s.n}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-white leading-tight">{s.t}</p>
                <p className="text-[11px] text-[#999] mt-0.5">{s.d}</p>
              </div>
            </li>
          ))}
        </ol>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={scrollToPoster}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold text-sm hover:scale-[1.03] hover:shadow-lg hover:shadow-purple-500/40 transition-all"
            data-testid="ai-explainer-cta"
          >
            <Upload className="w-4 h-4" /> Démarrer — téléverser mon affiche
          </button>
          <span className="text-xs text-[#888]">
            Pas d'affiche ? <button type="button" onClick={() => setExpanded(false)} className="underline hover:text-white" data-testid="ai-explainer-skip">remplissez à la main</button>
          </span>
        </div>
      </div>
    </div>
  );
}

